/**
 * Admin / JML Inbox — Track 5.4.
 *
 * Joiners / Movers / Leavers single-pane queue. The detector (cdm.jml.engine)
 * watches cdm_persons state transitions and writes one JmlEvent row per
 * (person × kind) detection. This page is the admin's "kill these 23 things"
 * console for executing the standard leaver flow in one click.
 *
 * Three tabs (Joiners / Movers / Leavers). Click a row → drawer with the
 * full person summary (accounts, identities, role grants, open compliance
 * findings, orphan-classifier proposals). Big red "Execute leaver" button
 * for leavers — runs cdm.jml.engine.execute_leaver and logs every action
 * taken. Joiners / movers fire a softer "acknowledge" path for now;
 * provisioning automation lands in Track 5.7 (SCIM).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Users, UserCheck, UserX, LogOut, ChevronLeft, RefreshCw, Play, X,
  AlertTriangle, CheckCircle2, Clock, ShieldOff, Tag, Ticket,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

type JmlKind = 'joiner' | 'mover' | 'leaver' | 'returning'
type JmlState = 'pending' | 'in_progress' | 'completed' | 'cancelled'

interface JmlEventListItem {
  id: string
  org_id: string
  person_id: string
  kind: JmlKind
  state: JmlState
  detected_at: string | null
  transition_window_ends_at: string | null
  seconds_remaining: number | null
  completed_at: string | null
  completed_by: string | null
  actions_taken_count: number
  notes: string | null
  person: {
    person_id: string | null
    full_name: string | null
    email: string | null
    department: string | null
    employment_status: string | null
    started_at: string | null
    left_at: string | null
    state: string | null
  }
}

interface JmlListResponse {
  count: number
  events: JmlEventListItem[]
  rollup: {
    pending_joiners: number
    pending_movers: number
    pending_leavers: number
    pending_returning: number
  }
}

interface PersonSummary {
  person: {
    person_id: string
    employee_id: string | null
    full_name: string | null
    email: string | null
    department: string | null
    manager_id: string | null
    employment_status: string | null
    started_at: string | null
    left_at: string | null
    state: string
  }
  accounts: Array<{
    user_id: string
    kind: string
    upn: string | null
    sam: string | null
    sid: string | null
    azure_oid: string | null
    state: string
  }>
  identities: Array<{
    id: string
    user_id: string
    kind: string
    fingerprint: string | null
    last_4: string | null
    issued_at: string | null
    expires_at: string | null
    revoked_at: string | null
  }>
  role_grants: Array<{
    id: string
    user_id: string
    role: string
    scope_kind: string
    scope_filter: any
    expires_at: string | null
    state: string
    reason: string
  }>
  open_findings: Array<{
    id: string
    finding_rule_id: string
    asset_id: string | null
    severity: string | null
    summary: string | null
    opened_at: string | null
  }>
  orphan_proposals: Array<{
    id: string
    target_id: string
    proposed_change: any
    proposer_reasoning: string | null
    created_at: string | null
  }>
  totals: {
    accounts: number
    identities: number
    active_grants: number
    open_findings: number
  }
}

interface JmlEventDetail extends JmlEventListItem {
  actions_taken: Array<{
    kind: string
    target: string
    executed_at: string
    by: string
    ok: boolean
    detail?: string
  }>
  summary: PersonSummary
}

const KIND_TONE: Record<JmlKind, string> = {
  joiner:    'bg-emerald/15 text-emerald border-emerald/30',
  mover:     'bg-amber/15 text-amber-light border-amber/30',
  leaver:    'bg-rose/15 text-rose-light border-rose/30',
  returning: 'bg-blue/15 text-blue-light border-blue/30',
}

const STATE_TONE: Record<JmlState, string> = {
  pending:     'bg-amber/15 text-amber-light border-amber/30',
  in_progress: 'bg-blue/15 text-blue-light border-blue/30',
  completed:   'bg-emerald/15 text-emerald border-emerald/30',
  cancelled:   'bg-text-muted/15 text-text-muted border-border',
}

type Tab = 'joiner' | 'mover' | 'leaver'

const TAB_DEFS: Array<{ id: Tab; label: string; icon: any; kind: JmlKind }> = [
  { id: 'joiner', label: 'Joiners', icon: UserCheck, kind: 'joiner' },
  { id: 'mover',  label: 'Movers',  icon: Users,     kind: 'mover' },
  { id: 'leaver', label: 'Leavers', icon: UserX,     kind: 'leaver' },
]

function fmtRemaining(secs: number | null): string {
  if (secs === null) return '—'
  if (secs <= 0) return 'overdue'
  const days = Math.floor(secs / 86400)
  const hours = Math.floor((secs % 86400) / 3600)
  if (days > 0) return `${days}d ${hours}h`
  const mins = Math.floor((secs % 3600) / 60)
  return `${hours}h ${mins}m`
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

export default function AdminJmlPage() {
  const [tab, setTab] = useState<Tab>('leaver')
  const [showCompleted, setShowCompleted] = useState(false)
  const [data, setData] = useState<JmlListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [selected, setSelected] = useState<JmlEventDetail | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [confirmExecute, setConfirmExecute] = useState(false)

  const load = () => {
    setLoading(true)
    const q = new URLSearchParams()
    q.set('kind', tab)
    if (!showCompleted) q.set('state', 'pending')
    api.get<JmlListResponse>(`/api/cdm/jml?${q.toString()}`)
      .then(r => setData(r.data))
      .catch(e => setMessage(`Load failed: ${e.response?.data?.detail || e.message}`))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [tab, showCompleted]) // eslint-disable-line react-hooks/exhaustive-deps

  const openDrawer = async (eventId: string) => {
    setDrawerLoading(true)
    setSelected(null)
    setConfirmExecute(false)
    try {
      const { data: detail } = await api.get<JmlEventDetail>(`/api/cdm/jml/${eventId}`)
      setSelected(detail)
    } catch (e: any) {
      setMessage(`Load detail failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setDrawerLoading(false)
    }
  }

  const triggerScan = async () => {
    setBusy('scan')
    setMessage('Running JML detector…')
    try {
      const { data } = await api.post('/api/cdm/jml/scan')
      const c = data?.counts || {}
      setMessage(`Scan complete: ${c.joiner ?? 0} joiners, ${c.mover ?? 0} movers, ${c.leaver ?? 0} leavers, ${c.returning ?? 0} returning`)
      load()
    } catch (e: any) {
      setMessage(`Scan failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setBusy(null)
    }
  }

  const executeEvent = async () => {
    if (!selected) return
    setBusy('execute')
    try {
      const { data: result } = await api.post(`/api/cdm/jml/${selected.id}/execute`)
      const sum = result.summary || {}
      const parts: string[] = []
      if (sum.grants_revoked) parts.push(`${sum.grants_revoked} grants revoked`)
      if (sum.accounts_disabled) parts.push(`${sum.accounts_disabled} accounts disabled`)
      if (sum.tag_backs) parts.push(`${sum.tag_backs} tag-backs queued`)
      if (sum.acknowledged) parts.push(`${sum.acknowledged} acknowledged`)
      setMessage(parts.length ? `Executed: ${parts.join(', ')}` : 'Executed (no actions required)')
      // Refetch detail + list
      await openDrawer(selected.id)
      load()
    } catch (e: any) {
      setMessage(`Execute failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setBusy(null)
      setConfirmExecute(false)
    }
  }

  const cancelEvent = async () => {
    if (!selected) return
    if (!window.confirm('Cancel this JML event? Use only when the HR feed misfired.')) return
    setBusy('cancel')
    try {
      await api.post(`/api/cdm/jml/${selected.id}/cancel`)
      setMessage('Event cancelled')
      await openDrawer(selected.id)
      load()
    } catch (e: any) {
      setMessage(`Cancel failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setBusy(null)
    }
  }

  const events = data?.events || []
  const rollup = data?.rollup
  const tabKind: JmlKind = tab

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/admin" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Admin
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Users size={18} className="text-cyan" /> JML INBOX
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          joiners · movers · leavers — HR-feed driven
        </span>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[11px] text-text-muted inline-flex items-center gap-1">
            <input type="checkbox" checked={showCompleted}
                   onChange={e => setShowCompleted(e.target.checked)} />
            show completed
          </label>
          <button type="button" onClick={triggerScan} disabled={!!busy} className="btn-ghost">
            <Play size={11} className={busy === 'scan' ? 'animate-pulse' : ''} /> Scan now
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      {/* Tab strip */}
      <div className="flex items-center gap-1 border-b border-border">
        {TAB_DEFS.map(t => {
          const Icon = t.icon
          const count = rollup
            ? (t.kind === 'joiner' ? rollup.pending_joiners
               : t.kind === 'mover' ? rollup.pending_movers
               : rollup.pending_leavers)
            : null
          return (
            <button key={t.id} type="button"
                    onClick={() => setTab(t.id)}
                    className={clsx(
                      'px-3 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2 -mb-px',
                      tab === t.id
                        ? 'text-text-primary border-cyan'
                        : 'text-text-muted border-transparent hover:text-text-secondary',
                    )}>
              <Icon size={12} />
              {t.label}
              {count !== null && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-surface-raised text-[10px] text-text-muted">
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Event list */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">State</th>
              <th className="text-left px-3 py-2">Person</th>
              <th className="text-left px-3 py-2">Department</th>
              <th className="text-left px-3 py-2">Detected</th>
              <th className="text-left px-3 py-2">Window</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {events.length === 0 && !loading && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-muted">
                No {TAB_DEFS.find(t => t.id === tab)?.label.toLowerCase()} events
                {!showCompleted ? ' pending' : ''}.
                Click 'Scan now' to walk cdm_persons for fresh state changes.
              </td></tr>
            )}
            {events.map(ev => (
              <tr key={ev.id}
                  className={clsx(
                    'cursor-pointer hover:bg-surface-raised/40',
                    selected?.id === ev.id && 'bg-surface-raised/60',
                  )}
                  onClick={() => openDrawer(ev.id)}>
                <td className="px-3 py-1.5">
                  <span className={clsx(
                    'px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider',
                    STATE_TONE[ev.state],
                  )}>
                    {ev.state}
                  </span>
                </td>
                <td className="px-3 py-1.5">
                  <div className="text-text-primary">{ev.person.full_name || '(no name)'}</div>
                  <div className="text-[10px] text-text-muted font-mono">{ev.person.email || '—'}</div>
                </td>
                <td className="px-3 py-1.5 text-text-secondary">{ev.person.department || '—'}</td>
                <td className="px-3 py-1.5 text-[10px] text-text-muted">{fmtDate(ev.detected_at)}</td>
                <td className="px-3 py-1.5 text-[11px]">
                  <span className={clsx(
                    'inline-flex items-center gap-1',
                    ev.seconds_remaining !== null && ev.seconds_remaining <= 0
                      ? 'text-rose-light' : 'text-text-secondary',
                  )}>
                    <Clock size={10} />
                    {fmtRemaining(ev.seconds_remaining)}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-[10px] text-text-muted">
                  {ev.actions_taken_count > 0
                    ? `${ev.actions_taken_count} taken`
                    : (ev.state === 'pending' ? 'review →' : '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Joiners panel — provisioning checklist placeholder */}
      {tab === 'joiner' && events.length > 0 && (
        <div className="card p-3 text-[11px] text-text-muted">
          <div className="text-text-secondary uppercase tracking-widest text-[10px] mb-1">Joiner provisioning checklist</div>
          <ul className="list-disc ml-5 space-y-0.5">
            <li>Provision Entra account · pending SCIM (Track 5.7)</li>
            <li>Issue laptop / hardware tokens · pending SCCM hook</li>
            <li>Default RBAC tier grant · auto-mint via Track 5.1 expirers</li>
            <li>Onboarding training · external system, surfaced read-only</li>
          </ul>
        </div>
      )}

      {/* Drawer */}
      {(selected || drawerLoading) && (
        <div className="fixed inset-y-0 right-0 w-full sm:w-[640px] z-40 bg-surface-card border-l border-border shadow-2xl overflow-y-auto">
          <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {drawerLoading && <RefreshCw size={14} className="animate-spin text-text-muted" />}
              {selected && (
                <>
                  <span className={clsx(
                    'px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider',
                    KIND_TONE[selected.kind],
                  )}>{selected.kind}</span>
                  <h2 className="text-sm font-bold text-text-primary truncate">
                    {selected.person.full_name || selected.person.email || selected.person.person_id}
                  </h2>
                </>
              )}
            </div>
            <button type="button" onClick={() => { setSelected(null); setConfirmExecute(false) }}
                    className="text-text-muted hover:text-text-primary">
              <X size={16} />
            </button>
          </div>

          {selected && (
            <div className="p-4 space-y-4">
              <DetailHeader event={selected} />

              {/* Action panel */}
              {selected.state === 'pending' && (
                <div className="card p-3 border-rose/30 bg-rose/5 space-y-2">
                  {selected.kind === 'leaver' ? (
                    <>
                      <div className="text-text-primary text-sm font-bold flex items-center gap-2">
                        <ShieldOff size={14} className="text-rose-light" />
                        Execute leaver flow
                      </div>
                      <p className="text-[11px] text-text-secondary">
                        This will revoke every active role grant ({selected.summary.totals.active_grants})
                        and disable every linked account ({selected.summary.totals.accounts}) for{' '}
                        <strong>{selected.person.full_name || selected.person.email}</strong>.
                        Tag-backs will be queued for source systems where capability is enabled.
                        This action is audited and irreversible.
                      </p>
                      {!confirmExecute ? (
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setConfirmExecute(true)}
                                  disabled={!!busy}
                                  className="px-3 py-1.5 text-xs rounded bg-rose hover:bg-rose-light text-white inline-flex items-center gap-1">
                            <UserX size={12} /> Execute leaver
                          </button>
                          <button type="button" onClick={cancelEvent} disabled={!!busy}
                                  className="btn-ghost text-[11px]">
                            Cancel event (HR misfire)
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2 items-center">
                          <span className="text-rose-light text-[11px] font-mono">Confirm — last chance:</span>
                          <button type="button" onClick={executeEvent} disabled={!!busy}
                                  className="px-3 py-1.5 text-xs rounded bg-rose hover:bg-rose-light text-white inline-flex items-center gap-1">
                            <LogOut size={12} /> Yes, kill it all
                          </button>
                          <button type="button" onClick={() => setConfirmExecute(false)}
                                  disabled={!!busy} className="btn-ghost text-[11px]">
                            Back
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-text-primary text-sm font-bold flex items-center gap-2">
                        <UserCheck size={14} className="text-amber-light" />
                        Acknowledge {selected.kind}
                      </div>
                      <p className="text-[11px] text-text-secondary">
                        For Phase-1, {selected.kind} events are tracked but require manual
                        access cleanup on the per-grant page. Click acknowledge to mark the
                        event as completed.
                      </p>
                      <div className="flex gap-2">
                        <button type="button" onClick={executeEvent} disabled={!!busy}
                                className="btn-primary text-[11px]">
                          Acknowledge
                        </button>
                        <button type="button" onClick={cancelEvent} disabled={!!busy}
                                className="btn-ghost text-[11px]">
                          Cancel event
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {selected.state === 'completed' && selected.actions_taken.length > 0 && (
                <div className="card p-3 space-y-2">
                  <div className="text-text-primary text-sm font-bold flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald" />
                    Actions taken
                  </div>
                  <ul className="text-[11px] space-y-0.5 font-mono">
                    {selected.actions_taken.map((a, i) => (
                      <li key={i} className={clsx('flex items-baseline gap-2',
                                                   a.ok ? 'text-text-secondary' : 'text-rose-light')}>
                        <span>{a.ok ? '✓' : '✗'}</span>
                        <span className="text-cyan">{a.kind}</span>
                        <span className="text-text-muted">{a.target.slice(0, 12)}</span>
                        {a.detail && <span className="text-text-muted">— {a.detail}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <SummarySection title={`Accounts (${selected.summary.totals.accounts})`}>
                {selected.summary.accounts.length === 0
                  ? <Empty msg="No CDM accounts linked." />
                  : (
                    <table className="w-full text-[11px]">
                      <thead className="text-[10px] uppercase tracking-widest text-text-muted">
                        <tr><th className="text-left">Kind</th><th className="text-left">UPN / SAM</th><th className="text-left">State</th></tr>
                      </thead>
                      <tbody>
                        {selected.summary.accounts.map(a => (
                          <tr key={a.user_id} className="border-t border-border/40">
                            <td className="py-0.5">{a.kind}</td>
                            <td className="py-0.5 font-mono">{a.upn || a.sam || '—'}</td>
                            <td className="py-0.5 text-text-muted">{a.state}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
              </SummarySection>

              <SummarySection title={`Identities (${selected.summary.totals.identities})`}>
                {selected.summary.identities.length === 0
                  ? <Empty msg="No active auth bindings." />
                  : (
                    <ul className="text-[11px] space-y-0.5 font-mono">
                      {selected.summary.identities.map(i => (
                        <li key={i.id} className="flex items-baseline gap-2">
                          <span className="text-cyan">{i.kind}</span>
                          <span className="text-text-muted">{i.fingerprint?.slice(0, 16) || '—'}</span>
                          {i.expires_at && <span className="text-text-muted">expires {fmtDate(i.expires_at)}</span>}
                          {i.revoked_at && <span className="text-rose-light">revoked</span>}
                        </li>
                      ))}
                    </ul>
                  )}
              </SummarySection>

              <SummarySection title={`Role grants (${selected.summary.totals.active_grants} active)`}>
                {selected.summary.role_grants.length === 0
                  ? <Empty msg="No role grants." />
                  : (
                    <table className="w-full text-[11px]">
                      <thead className="text-[10px] uppercase tracking-widest text-text-muted">
                        <tr><th className="text-left">Role</th><th className="text-left">Scope</th><th className="text-left">Expires</th><th className="text-left">State</th></tr>
                      </thead>
                      <tbody>
                        {selected.summary.role_grants.map(g => (
                          <tr key={g.id} className="border-t border-border/40">
                            <td className="py-0.5 font-mono">{g.role}</td>
                            <td className="py-0.5 text-text-muted">{g.scope_kind}</td>
                            <td className="py-0.5 text-text-muted">{fmtDate(g.expires_at)}</td>
                            <td className="py-0.5">
                              <span className={clsx('px-1.5 rounded text-[10px]',
                                g.state === 'active' ? 'bg-emerald/15 text-emerald'
                                  : g.state === 'revoked' ? 'bg-rose/15 text-rose-light'
                                  : 'bg-text-muted/15 text-text-muted')}>
                                {g.state}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
              </SummarySection>

              {selected.summary.open_findings.length > 0 && (
                <SummarySection title={`Open compliance findings (${selected.summary.open_findings.length})`}>
                  <ul className="text-[11px] space-y-0.5">
                    {selected.summary.open_findings.map(f => (
                      <li key={f.id} className="flex items-baseline gap-2">
                        <Ticket size={10} className="text-amber-light" />
                        <span className="font-mono">{f.finding_rule_id}</span>
                        {f.severity && <span className="text-text-muted">[{f.severity}]</span>}
                        <span className="text-text-secondary">{f.summary || ''}</span>
                      </li>
                    ))}
                  </ul>
                </SummarySection>
              )}

              {selected.summary.orphan_proposals.length > 0 && (
                <SummarySection title={`Orphan-classifier proposals (${selected.summary.orphan_proposals.length})`}>
                  <ul className="text-[11px] space-y-0.5">
                    {selected.summary.orphan_proposals.map(p => (
                      <li key={p.id} className="flex items-baseline gap-2">
                        <Tag size={10} className="text-blue-light" />
                        <span className="font-mono text-text-muted">{p.target_id.slice(0, 8)}…</span>
                        <span className="text-text-secondary">{p.proposer_reasoning?.slice(0, 80) || ''}</span>
                      </li>
                    ))}
                  </ul>
                </SummarySection>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}


function DetailHeader({ event }: { event: JmlEventDetail }) {
  return (
    <div className="card p-3 space-y-1">
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div><span className="text-text-muted">Email:</span> <span className="font-mono">{event.person.email || '—'}</span></div>
        <div><span className="text-text-muted">Department:</span> {event.person.department || '—'}</div>
        <div><span className="text-text-muted">Started:</span> {fmtDate(event.person.started_at)}</div>
        <div><span className="text-text-muted">Left:</span> {fmtDate(event.person.left_at)}</div>
        <div><span className="text-text-muted">Detected:</span> {fmtDate(event.detected_at)}</div>
        <div><span className="text-text-muted">Window ends:</span> {fmtDate(event.transition_window_ends_at)}</div>
        {event.completed_by && (
          <div className="col-span-2"><span className="text-text-muted">Completed by:</span> {event.completed_by} at {fmtDate(event.completed_at)}</div>
        )}
      </div>
      {event.seconds_remaining !== null && event.seconds_remaining <= 0 && (
        <div className="text-rose-light text-[11px] inline-flex items-center gap-1">
          <AlertTriangle size={11} /> Transition window expired — execute now.
        </div>
      )}
    </div>
  )
}

function SummarySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-3 space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{title}</div>
      {children}
    </div>
  )
}

function Empty({ msg }: { msg: string }) {
  return <div className="text-[11px] text-text-muted italic">{msg}</div>
}
